// apps/web/src/primitives/ErrorBoundary.tsx
//
// Global React error boundary mounted at the SPA root. Catches render-time
// exceptions from any descendant and shows a stable error frame instead of
// a blank white screen.
//
// 2026-05-26 (#101) — previously the SPA had NO error boundary. Any throw
// from a Zone component, TanStack hook deserialization mismatch, or
// transient render error would white-screen the entire dashboard with the
// stack trace landing only in DevTools. This boundary catches those, shows
// a stable error frame with a "reload" affordance, and surfaces the error
// (name + message + first-line of stack) so the user can paste the
// diagnostic into a bug report without leaving the page.
//
// Per [[feedback_no_fallbacks]]: the boundary is NOT a way to hide errors.
// It exists so the dashboard degrades visibly (not silently) AND the rest
// of the route tree stays responsive (so a broken zone doesn't take the
// whole SPA with it). Production telemetry is via console.error — the
// browser network panel + Cloudflare Pages analytics will surface it.

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  /** Optional label to identify which boundary caught the error (for nested
   *  use — top-level mounts won't pass this and the fallback says "the
   *  dashboard"). */
  scopeLabel?: string;
  children:    ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

const FRAME_STYLE = {
  background:   'var(--color-bg-2)',
  border:       '1px solid var(--color-fail)',
  borderRadius: 6,
  color:        'var(--color-text-1)',
  fontFamily:   'system-ui, -apple-system, sans-serif',
  margin:       '24px auto',
  maxWidth:     720,
  padding:      '20px 24px',
} as const;

const HEADING_STYLE = {
  color:      'var(--color-fail)',
  fontSize:   16,
  fontWeight: 600,
  margin:     '0 0 8px',
} as const;

const DETAIL_STYLE = {
  background:   'var(--color-bg-1)',
  border:       '1px solid var(--color-border)',
  borderRadius: 4,
  color:        'var(--color-text-2)',
  fontFamily:   'ui-monospace, SFMono-Regular, monospace',
  fontSize:     12,
  lineHeight:   1.4,
  margin:       '8px 0 16px',
  maxHeight:    200,
  overflow:     'auto',
  padding:      '8px 10px',
  whiteSpace:   'pre-wrap',
  wordBreak:    'break-word',
} as const;

const BUTTON_STYLE = {
  background:   'var(--color-bg-3)',
  border:       '1px solid var(--color-border)',
  borderRadius: 4,
  color:        'var(--color-text-1)',
  cursor:       'pointer',
  fontSize:     13,
  marginRight:  8,
  padding:      '6px 12px',
} as const;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Production telemetry: log via console.error so the browser console +
    // Cloudflare Pages Real-User-Monitoring (if enabled) surface it.
    // Use a stable kind tag so future grep/dashboard wiring can find it.
    console.error('[apps/web] react-error-boundary caught', {
      scope:         this.props.scopeLabel ?? 'root',
      name:          error.name,
      message:       error.message,
      stack:         error.stack,
      componentStack: info.componentStack,
    });
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;

    const scope = this.props.scopeLabel ?? 'the dashboard';
    const e = this.state.error;
    return (
      <div role="alert" style={FRAME_STYLE} data-testid="error-boundary-fallback">
        <h2 style={HEADING_STYLE}>Something went wrong in {scope}.</h2>
        <p style={{ margin: '0 0 8px', color: 'var(--color-text-2)', fontSize: 13 }}>
          The page hit an unexpected error. Reloading usually fixes it. If it
          persists, the diagnostic below helps file a precise bug.
        </p>
        <pre style={DETAIL_STYLE}>
          {e.name}: {e.message}
          {e.stack ? `\n\n${e.stack.split('\n').slice(0, 8).join('\n')}` : ''}
        </pre>
        <div>
          <button
            type="button"
            style={BUTTON_STYLE}
            onClick={() => window.location.reload()}
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>
          <button
            type="button"
            style={BUTTON_STYLE}
            onClick={() => this.setState({ error: null })}
            data-testid="error-boundary-retry"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
